import { Component, isDevMode, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { CoreConfigService } from "../../../../@core/services/config.service";
import { ActivatedRoute, Router } from "@angular/router";
import { first, takeUntil } from "rxjs/operators";
import { AuthenticationService, UserService } from "../../../auth/service";
import packageJson from "../../../../../package.json";
import { UtilsService } from "../../../auth/service/utils.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import * as moment from "moment/moment";
import { environment } from "../../../../environments/environment";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"]
})
export class LoginComponent implements OnInit {
  //  Public
  public coreConfig: any;
  public loginForm: FormGroup;
  public loading = false;
  public submitted = false;
  public returnUrl: string;
  public error = "";
  public passwordTextType: boolean;
  public sendPasswordResetTo: string = "";
  public isMaintenance: boolean = false;

  // Private
  private _unsubscribeAll: Subject<any>;

  public options: any;

  /**
   * Constructor
   *
   * @param {CoreConfigService} _coreConfigService
   * @param _formBuilder
   * @param _route
   * @param _router
   * @param _authenticationService
   * @param _utilsService
   * @param _modalService
   * @param _userService
   */
  constructor(
    private _coreConfigService: CoreConfigService,
    private _formBuilder: FormBuilder,
    private _route: ActivatedRoute,
    private _router: Router,
    private _authenticationService: AuthenticationService,
    public _utilsService: UtilsService,
    public _modalService: NgbModal,
    private _userService: UserService
  ) {
    this._unsubscribeAll = new Subject();

    // Configure the layout
    this._coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true
        },
        menu: {
          hidden: true
        },
        footer: {
          hidden: true
        },
        customizer: false,
        enableLocalStorage: false
      }
    };

    if (this._authenticationService.currentUserValue) {
      this._router.navigate(["/dashboard/main"]);
    }
  }

  /**
   * on init
   */
  async ngOnInit() {
    this.loginForm = this._formBuilder.group({
      email: ["", [Validators.required, Validators.email]],
      password: ["", Validators.required]
    });

    // get return url from route parameters or default to '/'
    this.returnUrl = this._route.snapshot.queryParams.returnUrl || "/dashboard/main";

    // Subscribe to config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.coreConfig = config;
    });
    this.options = await this._utilsService.basic_get("/options");
    if (!!this.options) {
      if (
        moment().format("YYYY-MM-DD HH:mm") >= this._utilsService.formatDate(this.options.maintenance_start, "YYYY-MM-DD HH:mm") &&
        moment().format("YYYY-MM-DD HH:mm") <= this._utilsService.formatDate(this.options.maintenance_end, "YYYY-MM-DD HH:mm") &&
        !environment.enableMaintenanceLogin
      ) {
        this.isMaintenance = true;
      }
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  /**
   * Toggle password
   */
  togglePasswordTextType() {
    this.passwordTextType = !this.passwordTextType;
  }

  onSubmit() {
    this.submitted = true;

    if (this.isMaintenance) {
      return false;
    }

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      return;
    }

    // Login
    this.loading = true;
    this._authenticationService
      .login(this.f.email.value, this.f.password.value)
      .pipe(first())
      .subscribe(
        () => {
          const search: any = new URLSearchParams(window.location.search.replace("?", ""));
          if (search.has("returnUrl")) {
            window.location.href = search.get("returnUrl");
          } else {
            setTimeout(() => window.location.reload(), 1000);
          }
        },
        (error) => {
          console.error(error);
          this.error = error;
          this.loading = false;
        }
      );
  }

  isDevMode() {
    return isDevMode();
  }

  autoLogin(selection: string) {
    switch (selection) {
      case "super-admin":
        this.loginForm.patchValue({ email: "m.candura@nepta.it", password: "De181f44897a587ce3!" });
        break;
      case "direzione":
        this.loginForm.patchValue({ email: "matteo.candura@nepta.it", password: "De181f44897a587ce3!" });
        break;
      case "commerciale":
        this.loginForm.patchValue({ email: "dipendente2@nepta.it", password: "08213A58ad009259ca$" });
        break;
      case "hr":
        this.loginForm.patchValue({ email: "davide.procaccini@nepta.it", password: "4d072950bf094d3edC%" });
        break;
      case "supporto-asset":
        this.loginForm.patchValue({ email: "arturo@nepta.it", password: "c14164717e253bFc22*" });
        break;
      case "supporto-piattaforma":
        this.loginForm.patchValue({ email: "e.salarini@nepta.it", password: "813ee2e99f5b73986C&" });
        break;
      case "recruiting":
        this.loginForm.patchValue({ email: "matteo.candura2@nepta.it", password: "B23167aa8f60cbf903#" });
        break;
      case "employee1":
        this.loginForm.patchValue({ email: "m.rossi@nepta.it", password: "prova1" });
        break;
      default:
        this.loginForm.patchValue({ email: "", password: "" });
        break;
    }
    this.onSubmit();
  }

  getVersion() {
    return packageJson.version;
  }

  public modalOpen(modalBasic) {
    this.sendPasswordResetTo = "";
    this._modalService.open(modalBasic, {
      size: "sm",
      centered: true
    });
  }

  async requireNewPassword() {
    if (!this.sendPasswordResetTo || (this.sendPasswordResetTo && this.sendPasswordResetTo.length < 5)) {
      return false;
    }
    const data = {
      email: this.sendPasswordResetTo.toLowerCase()
    };
    this._utilsService.showLoading();
    this._userService
      .resetPassword(data)
      .then(async () => {
        this._utilsService.hideLoading();
        this._modalService.dismissAll();
        await this._utilsService.showSWALSuccess({ text: "REQUEST_RESET_PASSWORD" });
      })
      .catch(async () => {
        this._utilsService.hideLoading();
        this._modalService.dismissAll();
        await this._utilsService.showSWALError({ text: "EMAIL_NOT_IN_DATABASE" });
      });
  }
}
